import { apiCredentialsService } from './apiService';

// Constants
const LANO_TOKEN_KEY = 'lanoToken';
const LANO_TOKEN_EXPIRY_KEY = 'lanoTokenExpiry';

/**
 * Lano service for handling Lano API calls
 */
export const lanoService = {
  /**
   * Save Lano token to localStorage with expiry time
   * @param {string} token - The authentication token
   * @param {number} expiresIn - Token expiry in seconds (default: 24 hours)
   */
  saveToken: (token, expiresIn = 86400) => {
    const now = new Date();
    const expiry = new Date(now.getTime() + expiresIn * 1000);
    
    localStorage.setItem(LANO_TOKEN_KEY, token);
    localStorage.setItem(LANO_TOKEN_EXPIRY_KEY, expiry.toISOString());
    
    // console.log(`Lano token saved, expires at ${expiry.toLocaleString()}`);
  },
  
  /**
   * Get Lano token from localStorage, returns null if expired
   * @returns {string|null} The token if valid, null if expired or not found
   */
  getStoredToken: () => {
    const token = localStorage.getItem(LANO_TOKEN_KEY);
    const expiry = localStorage.getItem(LANO_TOKEN_EXPIRY_KEY);
    
    if (!token || !expiry) {
      return null;
    }
    
    const now = new Date();
    const expiryDate = new Date(expiry);
    
    if (now >= expiryDate) {
      // Token has expired, clear it
      localStorage.removeItem(LANO_TOKEN_KEY);
      localStorage.removeItem(LANO_TOKEN_EXPIRY_KEY);
      return null;
    }
    
    return token;
  },
  
  /**
   * Clear Lano token from localStorage
   */
  clearToken: () => {
    localStorage.removeItem(LANO_TOKEN_KEY);
    localStorage.removeItem(LANO_TOKEN_EXPIRY_KEY);
  },

  /**
   * Get Lano authentication token
   * @param {boolean} forceRefresh - Force a new token even if one exists
   * @returns {Promise<string>} The authentication token
   */
  getAuthToken: async (forceRefresh = false) => {
    try {
      // Check if we have a valid token in localStorage
      if (!forceRefresh) {
        const storedToken = lanoService.getStoredToken();
        if (storedToken) {
          // console.log('Using existing Lano token from localStorage');
          return storedToken;
        }
      }
      
      // Get Lano credentials from our API
      const response = await apiCredentialsService.getLanoCredentials();
      
      if (!response.data || !response.data.success) {
        throw new Error('Failed to fetch Lano credentials');
      }
      
      const credentials = response.data.credential;
      
      // Get Lano authentication token using the credentials
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        "grant_type": "password",
        "client_id": credentials.client_id,
        "client_secret": credentials.client_secret,
        "username": credentials.login,
        "password": credentials.password
      });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw,
        redirect: 'follow'
      };

      const tokenResponse = await fetch("https://api.lano.io/oauth/token", requestOptions);
      const result = await tokenResponse.json();
      
      if (!result.access_token) {
        throw new Error('Failed to get Lano token');
      }
      
      // Save token to localStorage with expiry time
      lanoService.saveToken(result.access_token, result.expires_in || 86400);
      
      return result.access_token;
    } catch (error) {
      console.error('Error getting Lano auth token:', error);
      throw error;
    }
  },
  
  /**
   * Get all tasks from Lano
   * @param {string} token - The Lano authentication token
   * @param {number} pageSize - The number of tasks per page
   * @returns {Promise<Object>} The tasks data
   */
  getAllTasks: async (token, pageSize = 100) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };

      const response = await fetch(`https://api.lano.io/api/v1/tasks?page[size]=${pageSize}`, requestOptions);
      return await response.json();
    } catch (error) {
      console.error('Error fetching Lano tasks:', error);
      throw error;
    }
  },
  
  /**
   * Get task by ID
   * @param {string} token - The Lano authentication token
   * @param {string} taskId - The task ID
   * @returns {Promise<Object>} The task data
   */
  getTaskById: async (token, taskId) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json; charset=utf-8");
      myHeaders.append("Authorization", `Bearer ${token}`);
      
      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };
      
      const response = await fetch(`https://api.lano.io/api/v1/tasks/${taskId}`, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch task: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching task ${taskId}:`, error);
      throw error;
    }
  },
  
  /**
   * Update a task
   * @param {string} token - The Lano authentication token
   * @param {string} taskId - The task ID
   * @param {Object} taskData - The updated task data
   * @returns {Promise<Object>} The updated task
   */
  updateTask: async (token, taskId, taskData) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);
      
      const requestOptions = {
        method: 'PATCH',
        headers: myHeaders,
        body: JSON.stringify({ task: taskData }),
        redirect: 'follow'
      };
      
      const response = await fetch(`https://api.lano.io/api/v1/tasks/${taskId}`, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to update task: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating task ${taskId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get company freelancers
   * @param {string} token - The Lano authentication token
   * @param {number} pageSize - The number of freelancers per page
   * @returns {Promise<Object>} The freelancers data
   */
  getCompanyFreelancers: async (token, pageSize = 1500) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);
      
      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };
      
      const response = await fetch(`https://api.lano.io/api/v1/company_freelancers?page[size]=${pageSize}`, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch freelancers: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching company freelancers:', error);
      throw error;
    }
  },
  
  /**
   * Get freelancer by ID
   * @param {string} token - The Lano authentication token
   * @param {string} freelancerId - The freelancer ID
   * @returns {Promise<Object>} The freelancer data
   */
  getFreelancerById: async (token, freelancerId) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json; charset=utf-8");
      myHeaders.append("Authorization", `Bearer ${token}`);
      
      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };
      
      const response = await fetch(`https://api.lano.io/api/v1/company_freelancers/${freelancerId}`, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch freelancer: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching freelancer ${freelancerId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get company freelancers from a specific page URL
   * @param {string} token - The Lano authentication token
   * @param {string} pageUrl - The URL of the page to fetch
   * @returns {Promise<Object>} The freelancers data
   */
  getCompanyFreelancersPage: async (token, pageUrl) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);
      
      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };
      
      const response = await fetch(pageUrl, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch freelancers page: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching company freelancers page:', error);
      throw error;
    }
  },
  
  /**
   * Create a new task
   * @param {string} token - The Lano authentication token
   * @param {Object} taskData - The task data
   * @returns {Promise<Object>} The created task
   */
  createTask: async (token, taskData) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);
      
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify({ task: taskData }),
        redirect: 'follow'
      };
      
      const response = await fetch("https://api.lano.io/api/v1/tasks", requestOptions);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 LANO API ERROR RESPONSE:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to create task: ${response.status} - ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },
  
  /**
   * Search company freelancers
   * @param {string} token - The Lano authentication token
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Object>} The search results
   */
  searchCompanyFreelancers: async (token, searchParams = {}) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);
      
      let url = "https://api.lano.io/api/v1/company_freelancers";
      
      // Add search parameters if provided
      if (Object.keys(searchParams).length > 0) {
        const queryParams = new URLSearchParams();
        
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value) {
            queryParams.append(`filter[${key}]`, value);
          }
        });
        
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
      }
      
      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow'
      };
      
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to search freelancers: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching company freelancers:', error);
      throw error;
    }
  },
  
  /**
   * Ensure a valid token is available
   * This is a convenience method that checks if a valid token exists,
   * and if not, gets a new one
   * 
   * @returns {Promise<string>} A valid Lano authentication token
   */
  ensureToken: async () => {
    const storedToken = lanoService.getStoredToken();
    
    if (storedToken) {
      return storedToken;
    }
    
    // No valid token in storage, get a new one
    return await lanoService.getAuthToken(true);
  }
}; 